/**
 * Image Utilities
 * Handles image loading errors with proxy fallback for bot-protected CDNs
 */

import { API_URL } from '../config/api.js';

// Branded placeholder matching Fantomize design (hexagon pattern)
export const PLACEHOLDER_IMAGE = '/placeholder-product.svg';

/**
 * Handle image loading errors with proxy retry.
 * On first error: retries through backend proxy to bypass bot protection.
 * On second error: falls back to branded placeholder.
 *
 * @param {Event} e - The error event from img element
 * @param {string} originalSrc - The original image URL (optional, falls back to current src)
 */
export const handleImageError = (e, originalSrc) => {
  const img = e.target;

  // Prevent infinite loops
  if (!img || img.dataset.errorHandled === 'placeholder') {
    return;
  }

  // Check if we already tried the proxy
  if (img.dataset.proxyAttempted === 'true') {
    // Proxy also failed, use placeholder
    console.log('[ImageUtils] Proxy failed, using placeholder for:', originalSrc || 'unknown');
    img.src = PLACEHOLDER_IMAGE;
    img.dataset.errorHandled = 'placeholder';
    img.onerror = null; // Prevent further errors
    return;
  }

  // Get the original source
  const srcToProxy = originalSrc || img.dataset.originalSrc || img.src;

  // Don't proxy already-proxied URLs or placeholder
  if (srcToProxy.includes('/api/image-proxy') || srcToProxy.includes('placeholder')) {
    img.src = PLACEHOLDER_IMAGE;
    img.dataset.errorHandled = 'placeholder';
    img.onerror = null;
    return;
  }

  // Mark that we're attempting proxy
  img.dataset.proxyAttempted = 'true';
  img.dataset.originalSrc = srcToProxy;

  // Try through backend proxy
  const proxyUrl = `${API_URL}/api/image-proxy?url=${encodeURIComponent(srcToProxy)}`;
  console.log('[ImageUtils] Retrying via proxy:', srcToProxy.substring(0, 50) + '...');
  img.src = proxyUrl;
};

/**
 * Get props object for img elements with automatic error handling.
 *
 * @param {string} src - The image source URL
 * @param {string} alt - Alt text for the image
 * @returns {object} Props object to spread on img element
 */
export const getImageProps = (src, alt = '') => ({
  src: src || PLACEHOLDER_IMAGE,
  alt,
  'data-original-src': src,
  onError: (e) => handleImageError(e, src),
});

export default {
  PLACEHOLDER_IMAGE,
  handleImageError,
  getImageProps,
};


